import React from "react";

/**
 * The row that switches between views INSIDE a page (T13-M3).
 *
 * Three screens grew their own version of this — the job filters, the quote
 * filters, and the settings tabs — and on a phone all three wrapped onto two
 * and three lines, which is how a filter row stops reading as navigation and
 * starts reading as a paragraph of buttons. Jorge's decision after the walk
 * through Bloque A: one line, scrolled sideways, with the cut visible so the
 * reader knows there is more.
 *
 * **The shape is Settings' own**, not a new one: a rule under the row and the
 * current view as a solid dark pill. That language was already in this repo
 * and already read as internal navigation, so the other two rows move to it
 * rather than a fourth thing being invented. `.chip` is deliberately left
 * alone — it labels a category on the quote cards, where it is not a control
 * at all, and restyling it here would have reached that.
 *
 * **Only below 992px.** Above it there is room for the row to sit on one line
 * by itself, and a scroller there would be a scroller nobody needs. The
 * wrapping row is what desktop keeps.
 *
 * The affordance is measured, not assumed. A gradient pinned to the right edge
 * whether or not anything is cut off is an affordance that lies, and after the
 * fourth one you stop believing it — so the fades are driven from
 * `scrollLeft`/`scrollWidth` and each edge appears only when there is content
 * past it.
 */
export default function ViewNav({ items, active, onSelect, testId, className = "" }) {
  const scroller = React.useRef(null);
  const [edges, setEdges] = React.useState({ start: false, end: false });

  const measure = React.useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // 1px of slack: a fractional scrollWidth would otherwise leave the end
    // fade painted forever on a row that is already fully scrolled.
    setEdges({ start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 });
  }, []);

  React.useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // The row's width changes with the viewport and its content changes with
    // the language, and both change whether anything is cut off.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, items]);

  /* The current view has to be ON SCREEN in a row that scrolls, or the one
     thing the row exists to tell you — which view you are in — is the thing
     hidden off its edge. Arriving at `?tab=danger` is exactly that case. */
  React.useEffect(() => {
    const el = scroller.current;
    const current = el?.querySelector('[data-active="true"]');
    if (current) current.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  const fade = (side) => ({
    position: "absolute",
    top: 0,
    bottom: "0.75rem",          // clears the row's own rule (pb-3)
    [side]: 0,
    width: 28,
    pointerEvents: "none",
    background: `linear-gradient(to ${side === "left" ? "right" : "left"}, var(--canvas), transparent)`,
  });

  return (
    <div className={`relative border-b border-sand pb-3 ${className}`}>
      <div
        ref={scroller}
        onScroll={measure}
        className="view-nav flex gap-2"
        data-testid={testId}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const on = item.key === active;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              aria-current={on ? "page" : undefined}
              data-active={on ? "true" : "false"}
              data-testid={item.testId}
              className={`shrink-0 px-3 py-2 rounded-sm text-sm font-display font-semibold flex items-center gap-1.5 transition ${
                on ? "bg-worker text-cream" : "bg-cream border border-sand text-worker hover:bg-linen"
              }`}
            >
              {Icon ? <Icon size={14} /> : null}
              {item.label}
            </button>
          );
        })}
      </div>
      {edges.start && <span style={fade("left")} aria-hidden="true" />}
      {edges.end && <span style={fade("right")} aria-hidden="true" />}
    </div>
  );
}
